import { _ } from "../../polyglot/polyglot.js";
import { endOfMonth, parse, startOfYear } from "../../helpers/date-util.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import ActivePeriodPickerWidget from "../../widgets/import/ActivePeriodPickerWidget/ActivePeriodPickerWidget.js";
import Budget from "../../../models/Budget.js";
import BudgetPickerWidget from "../../widgets/import/BudgetPickerWidget.js";
import ChartOfAccountPicker from "../../widgets/import/ChartOfAccountPicker.js";
import CurrencyPicker from "../../widgets/import/CurrencyPicker.js";
import FiscalYear from "../../../models/period/FiscalYear.js";
import FiscalYearsList from "../../widgets/import/FiscalYearsList.js";
import LastTransactionDatePickerWidget from "../../widgets/import/LastTransactionDatePickerWidget.js";
import optionsBag from "../../../models/options/optionsBag.js";
import Timespan from "../../../models/period/Timespan.js";

/**
 * `ConfigureImportDataConfigurationWidget` renders a bunch of options to
 * configure import data, right before the data is added to the
 * company.
 *
 * The options the user can choose from include:
 * - date range: from when to when to import data
 * - chart of accounts: the chart of accounts to use for the imported fiscal year
 * - currency: the currency to use for the transactions
 * - fiscal years: the fiscal years dimension objects to import
 * - budget: which budget to use. It can be no budget, a new one, or override an existing one.
 *
 * @param {boolean} [canCreateLightCompany] - Should be true iff the
 *   current user is allowed to create a light company with the imported
 *   data. This is necessary for a "Light company" checkbox to be
 *   visible
 * @param {boolean} [isExistingCompany] - Should be true iff the
 *   data being imported is for an existing company in the current
 *   agency
 * @param {boolean} [isExistingLightCompany] - Should be true iff the
 *   data being imported is for an existing company in the current
 *   agency and the company is light
 * @param {boolean} [wantExtendedActions] - Should be true iff the
 *   data imported needs the Extended Actions option in the last step of the import.
 * @param {boolean} [wantChartOfAccountsConfiguration] - Should be
 *   true iff the chart of accounts section must be visible
 * @param {boolean} [wantsBudgetPicker] - Should be true if the integration importer
 *   might contain some budget data that we want to import using the budget picker
 * @param {ChartOfAccounts[]} [chartsOfAccounts] - List of available chart of
 *   accounts.
 * @param {boolean} [selectOnlyLatestFiscalYear] - When this option is true, only
 *   the latest fiscal year is selected, otherwise all the fiscal years will be
 *   selected.
 */
export default class ConfigureImportDataConfigurationWidget extends Widget {
	constructor({
		canCreateLightCompany = false,
		isExistingCompany = false,
		isExistingLightCompany = false,
		wantExtendedActions = false,
		wantChartOfAccountsConfiguration = true,
		wantsBudgetPicker = false,
		chartsOfAccounts = FiscalYear.getChartOfAccounts(),
		importData,
		selectOnlyLatestFiscalYear = false,
	}) {
		super(...arguments);

		this._canCreateLightCompany = canCreateLightCompany;
		this._isExistingCompany = isExistingCompany;
		this._isExistingLightCompany = isExistingLightCompany;

		this._shouldImportAsLightCompany =
			canCreateLightCompany && isExistingLightCompany;

		this._wantExtendedActions = wantExtendedActions;

		this._wantChartOfAccountsConfiguration =
			wantChartOfAccountsConfiguration;

		this._defaultChartOfAccountsConfiguration = undefined;
		this._wantsBudgetPicker = wantsBudgetPicker;
		this._chartsOfAccounts = chartsOfAccounts;
		this._selectOnlyLatestFiscalYear = selectOnlyLatestFiscalYear;

		this._importData = importData;
		const companyInfo = importData.importCompanyInfo;

		this._createFiscalYearList({ companyInfo });

		const lastTransactionDate = parse(companyInfo.lastTransactionDate);
		const transactionTimespan = this._timespanFromStartOfYear(
			companyInfo,
			lastTransactionDate,
		);

		this._createLastTransactionDatePicker({
			transactionTimespan,
			lastTransactionDate,
		});

		this._createActivePeriodPicker({ companyInfo, lastTransactionDate });
		this._createChartOfAccountsPicker({ companyInfo, importData });
		this._createCurrencyPicker({ companyInfo });
		this._createBudgetPicker({ companyInfo, importData });
	}

	/**
	 * Returns true iff `canCreateLightCompany` is truthy and:
	 * - the company already exists and is a light company, or
	 * - the company does not exist yet and the user checked the "Light company" checkbox
	 */
	shouldImportAsLightCompany() {
		return this._shouldImportAsLightCompany;
	}

	getChartOfAccounts() {
		if (this._wantChartOfAccountsConfiguration) {
			return this._chartOfAccountsPicker.getChartOfAccount();
		}

		return this._defaultChartOfAccountsConfiguration;
	}

	getCurrency() {
		return this._currencyPicker.getCurrency();
	}

	getFiscalYears() {
		return this._fiscalYearsList.getFiscalYears();
	}

	getBudget() {
		if (!this._wantsBudgetPicker) {
			return undefined;
		}

		return this._budgetPicker.getBudget();
	}

	getLastTransactionDate() {
		return this._lastTransactionDatePicker.getSelectedDate();
	}

	getLastActiveDate() {
		return optionsBag.fromId("active-period").when({
			active: () => this._activePeriodPicker.getSelection(),
			inactive: () => endOfMonth(this.getLastTransactionDate()),
		});
	}

	validate(...rest) {
		if (!this._wantsBudgetPicker) {
			return;
		}
		this._budgetPicker.validate(...rest);
	}

	renderContentOn(html) {
		html.div(
			{ klass: "sie-import-step select-data-step" },
			html.div(
				{
					klass: "form form-horizontal",
					role: "form",
				},
				this._lastTransactionDatePicker,
				this._activePeriodPicker,
				this._chartOfAccountsPicker,
				this._currencyPicker,
				this._fiscalYearsList,
				this._budgetPicker,
				this._renderLightCompanyCheckboxOn.bind(this),
			),
		);
	}

	_renderLightCompanyCheckboxOn(html) {
		if (!this._shouldShowIsLightCompanyCheckBox()) {
			return;
		}

		html.div(
			{ klass: "form-group light-company-checkbox" },
			html.label({ klass: "control-label col-sm-4" }, _("Light Company")),
			html.div(
				{ klass: "col-sm-8 checkbox" },
				html
					.input({
						type: "checkbox",
					})
					.on("change", () => {
						this._shouldImportAsLightCompany =
							!this._shouldImportAsLightCompany;
					}),
			),
		);
	}

	_didAttach() {
		if (!this._wantChartOfAccountsConfiguration) {
			return;
		}
		const companyInfo = this._importData.importCompanyInfo;
		const defaultChartOfAccountsId =
			companyInfo.fiscalYears[0].chartOfAccountsId;

		let chartOfAccountsList = this._importData.chartOfAccounts;
		if (!chartOfAccountsList?.length) {
			chartOfAccountsList = this._chartsOfAccounts;
		}

		this._chartOfAccountsPicker.setChartOfAccounts(
			chartOfAccountsList,
			defaultChartOfAccountsId,
		);

		this._chartOfAccountsPicker.setImportCompanyInfo(companyInfo);

		if (this._wantExtendedActions) {
			this._chartOfAccountsPicker.showExtendedActions();
		}
	}

	_shouldShowIsLightCompanyCheckBox() {
		return this._canCreateLightCompany && !this._isExistingCompany;
	}

	_createLastTransactionDatePicker({
		transactionTimespan,
		lastTransactionDate,
	}) {
		this._lastTransactionDatePicker = new LastTransactionDatePickerWidget();
		this._lastTransactionDatePicker.setSelectableDates(transactionTimespan);

		this._lastTransactionDatePicker.setLastTransactionDate(
			lastTransactionDate,
		);
	}

	_createActivePeriodPicker({ companyInfo, lastTransactionDate }) {
		if (!optionsBag.fromId("active-period").isActive()) {
			this._activePeriodPicker = () => {};
			return;
		}

		this._activePeriodPicker = new ActivePeriodPickerWidget();
		const transactionTimespan = new Timespan({
			start: parse(companyInfo.fiscalYears[0].start),
			end: endOfMonth(lastTransactionDate),
		});

		this._activePeriodPicker.setSelectableMonths({
			transactionTimespan,
			fiscalYearEnd: parse(companyInfo.fiscalYears[0].end),
		});

		this._lastTransactionDatePicker.onDateChanged.register((date) => {
			const timespan = new Timespan({
				start: startOfYear(date),
				end: endOfMonth(
					this._lastTransactionDatePicker.getSelectedDate(),
				),
			});

			this._activePeriodPicker.setSelectableMonths({
				transactionTimespan: timespan,
				fiscalYearEnd: parse(companyInfo.fiscalYears[0].end),
			});
		});
	}

	_createChartOfAccountsPicker({ companyInfo, importData }) {
		if (!this._wantChartOfAccountsConfiguration) {
			this._chartOfAccountsPicker = () => {};

			this._defaultChartOfAccountsConfiguration =
				ChartOfAccountPicker.defaultChartOfAccountsConfiguration({
					companyInfo,
					chartOfAccounts: importData.chartOfAccounts,
				});

			return;
		}

		this._chartOfAccountsPicker = new ChartOfAccountPicker({
			showExtendedActions: this._wantExtendedActions,
		});
	}

	_createCurrencyPicker({ companyInfo }) {
		const currency = companyInfo.fiscalYears[0].currencyCultureName;

		this._currencyPicker = new CurrencyPicker();
		this._currencyPicker.selectCurrency(currency);
	}

	_createFiscalYearList({ companyInfo }) {
		this._fiscalYearsList = new FiscalYearsList({
			selectOnlyLatestFiscalYear: this._selectOnlyLatestFiscalYear,
		});

		this._fiscalYearsList.setFiscalYears(companyInfo.fiscalYears);
	}

	_createBudgetPicker({ companyInfo, importData }) {
		if (!this._wantsBudgetPicker || !companyInfo.hasBudget) {
			this._budgetPicker = () => {};
			this._wantsBudgetPicker = false;
			return;
		}

		const context = {
			fiscalYears: FiscalYear.materializeAll(companyInfo.fiscalYears),
		};

		const budgets = Budget.materializeAll(importData.budgets, context);

		this._budgetPicker = new BudgetPickerWidget({ budgets });
	}

	_timespanFromStartOfYear(companyInfo, end) {
		return new Timespan({
			start: parse(companyInfo.fiscalYears[0].start),
			end: endOfMonth(end),
		});
	}
}
